import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  LogIn, Eye, EyeOff, Mail, Phone, ShieldCheck, Activity, Layers, Lock,
} from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user && typeof user === "object") {
    return <Navigate to="/organizations" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Signed in successfully");
      navigate("/organizations");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50">
      <div className="mx-auto grid min-h-screen max-w-[1400px] grid-cols-1 lg:grid-cols-[1.05fr_1fr]">

        {/* ---------- LEFT: Brand / value panel ---------- */}
        <aside className="relative hidden overflow-hidden bg-[#0B1437] lg:flex lg:flex-col lg:justify-between">
          {/* decorative grid + glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-32 -left-20 h-[480px] w-[480px] rounded-full bg-[#3B5BFD] opacity-30 blur-[120px]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#7C3AED] opacity-25 blur-[120px]"
          />

          {/* Top: brand */}
          <div className="relative z-10 flex items-center gap-3 px-12 pt-12">
            <div className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-gradient-to-br from-[#3B5BFD] to-[#7C3AED] shadow-lg shadow-indigo-900/40">
              <span className="text-lg font-bold tracking-tight text-white">S</span>
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight text-white">
                Shivonix Technologies
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-indigo-200/70">
                Publication Suite · SaaS
              </div>
            </div>
          </div>

          {/* Middle: headline + features */}
          <div className="relative z-10 px-12">
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-indigo-100/90 backdrop-blur">
              <ShieldCheck size={11} /> Built for high-trust workflows
            </div>
            <h1 className="text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-[44px]">
              Run your publishing business{" "}
              <span className="bg-gradient-to-r from-indigo-300 to-fuchsia-300 bg-clip-text text-transparent">
                end-to-end.
              </span>
            </h1>
            <p className="mt-5 max-w-md text-[15px] leading-relaxed text-indigo-100/70">
              Organizations, publication houses, periodicals, subscriptions,
              renewals, dispatch & GST-compliant receipts — one console, audited
              every step.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-indigo-100/85">
              <Feature icon={<Layers size={14} />} text="Org → Publication → Periodical hierarchy" />
              <Feature icon={<Activity size={14} />} text="Auto GST tax invoices · FY auto-roll" />
              <Feature icon={<Lock size={14} />} text="IP-stamped audit trail · soft-delete safe" />
            </ul>
          </div>

          {/* Bottom: contact + legal */}
          <div className="relative z-10 px-12 pb-12">
            <div className="rounded-md border border-white/10 bg-white/[0.04] p-5 backdrop-blur-sm">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-indigo-200/70">
                Subscribe to this platform
              </div>
              <div className="mt-2 grid grid-cols-1 gap-1.5 text-sm text-white/90 sm:grid-cols-2">
                <a
                  href="mailto:admin@shivonix.com"
                  className="inline-flex items-center gap-2 hover:text-indigo-300"
                  data-testid="login-contact-email"
                >
                  <Mail size={14} className="text-indigo-300" />
                  admin@shivonix.com
                </a>
                <a
                  href="tel:+917719066290"
                  className="inline-flex items-center gap-2 hover:text-indigo-300"
                  data-testid="login-contact-phone"
                >
                  <Phone size={14} className="text-indigo-300" />
                  +91 77190 66290
                </a>
              </div>
            </div>
            <div className="mt-5 text-[11px] text-indigo-200/50">
              © {new Date().getFullYear()} Shivonix Technologies — Software, IT Services & BPO ·
              All audit events are stamped with IP & user metadata.
            </div>
          </div>
        </aside>

        {/* ---------- RIGHT: Sign-in card ---------- */}
        <main className="relative flex items-center justify-center bg-white px-6 py-10 lg:px-12">
          {/* Mobile brand strip */}
          <div className="absolute left-6 top-6 flex items-center gap-2 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-gradient-to-br from-[#3B5BFD] to-[#7C3AED]">
              <span className="text-sm font-bold text-white">S</span>
            </div>
            <span className="text-sm font-semibold tracking-tight text-slate-900">
              Shivonix Technologies
            </span>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="login-form"
            className="w-full max-w-[420px]"
          >
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#3B5BFD]">
              Sign in
            </div>
            <h2 className="text-[32px] font-semibold leading-tight tracking-tight text-slate-900">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Access your publication command centre. Sessions expire automatically
              after 12 hours.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <Label htmlFor="email" className="text-[13px] font-medium text-slate-700">
                  Work email <span className="text-rose-600">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="mt-1.5 h-11 rounded-md border-slate-300 placeholder:text-slate-400 focus-visible:border-[#3B5BFD] focus-visible:ring-[#3B5BFD]/20"
                  placeholder="name@company.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-baseline justify-between">
                  <Label htmlFor="password" className="text-[13px] font-medium text-slate-700">
                    Password <span className="text-rose-600">*</span>
                  </Label>
                  <span className="text-[11px] text-slate-400">12-hour secure session</span>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11 rounded-md border-slate-300 pr-10 placeholder:text-slate-400 focus-visible:border-[#3B5BFD] focus-visible:ring-[#3B5BFD]/20"
                    placeholder="••••••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    data-testid="login-toggle-password"
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 transition hover:text-slate-700"
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error ? (
                <div
                  data-testid="login-error"
                  role="alert"
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700"
                >
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="h-11 w-full rounded-md bg-[#0B1437] text-[14px] font-medium text-white shadow-sm transition hover:bg-[#1A2454] disabled:opacity-60"
              >
                <LogIn size={16} className="mr-2" />
                {submitting ? "Signing in…" : "Sign in to dashboard"}
              </Button>
            </div>

            <div className="mt-7 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400">Need access?</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            <p className="mt-4 text-center text-[13px] text-slate-600">
              For onboarding, plan & subscription pricing, contact{" "}
              <a href="mailto:admin@shivonix.com" className="font-medium text-[#3B5BFD] hover:underline" data-testid="login-help-email">
                admin@shivonix.com
              </a>{" "}
              or call{" "}
              <a href="tel:+917719066290" className="font-medium text-[#3B5BFD] hover:underline" data-testid="login-help-phone">
                +91 77190 66290
              </a>
              .
            </p>

            {/* Mobile-only contact card */}
            <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4 text-[11px] text-slate-500 lg:hidden">
              © {new Date().getFullYear()} Shivonix Technologies — Software, IT Services & BPO.
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-indigo-200 ring-1 ring-inset ring-white/10">
        {icon}
      </span>
      <span>{text}</span>
    </li>
  );
}
